import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../contexts/LanguageContext';

const Intro = () => {
    const { t } = useTranslation();

    return (
        <div className="py-16 bg-white" id="about">
            <div className="m-auto max-w-6xl p-2 md:p-12">
                <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
                    {/* Left Side - Feature Grid */}
                    <div className="lg:w-1/2 w-full">
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 shadow-xl">
                            <div className="grid grid-cols-2 gap-6">
                                {/* Visibility Card */}
                                <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100">
                                    <div className="bg-blue-100 w-12 h-12 rounded-2xl flex items-center justify-center mb-3">
                                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    </div>
                                    <h4 className="font-semibold text-gray-900 text-lg">
                                        {t('intro.visibility.title', 'Complete Visibility')}
                                    </h4>
                                </div>
                                
                                {/* Save Money Card */}
                                <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100">
                                    <div className="bg-green-100 w-12 h-12 rounded-2xl flex items-center justify-center mb-3">
                                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                        </svg>
                                    </div>
                                    <h4 className="font-semibold text-gray-900 text-lg">
                                        {t('common.save', 'Save')} {t('common.price', 'Price')}
                                    </h4>
                                </div>
                                
                                {/* Sync Everywhere Card */}
                                <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100">
                                    <div className="bg-purple-100 w-12 h-12 rounded-2xl flex items-center justify-center mb-3">
                                        <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                        </svg>
                                    </div>
                                    <h4 className="font-semibold text-gray-900 text-lg">
                                        {t('features.crossPlatform.title', 'Cross-Platform')}
                                    </h4>
                                </div>
                                
                                {/* Share Plans Card */}
                                <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100">
                                    <div className="bg-orange-100 w-12 h-12 rounded-2xl flex items-center justify-center mb-3">
                                        <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                    </div>
                                    <h4 className="font-semibold text-gray-900 text-lg">
                                        {t('features.sharingSpace.title', 'Sharing Space')}
                                    </h4>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Right Side - Content */}
                    <div className="lg:w-1/2 w-full text-center lg:text-left">
                        <h2 className="text-4xl font-bold text-blue-900 mb-6">
                            {t('intro.title', 'Why Choose EasySubs?')}
                        </h2>
                        
                        <div className="space-y-6 mb-8">
                            {/* Complete Visibility */}
                            <div className="flex items-start">
                                <div className="bg-blue-100 p-3 rounded-lg mr-4 flex-shrink-0">
                                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                        {t('intro.visibility.title', 'Complete Visibility')}
                                    </h3>
                                    <p className="text-gray-600">
                                        {t('intro.visibility.description', 'See all your subscriptions in one dashboard. Track spending, renewal dates, and usage patterns easily.')}
                                    </p>
                                </div>
                            </div>

                            {/* Catch Promo & Special Prices */}
                            <div className="flex items-start">
                                <div className="bg-orange-100 p-3 rounded-lg mr-4 flex-shrink-0">
                                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-5 5v-5zM4.93 4.93l9.07 9.07-9.07 9.07L4.93 4.93z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                        {t('intro.promoAlerts.title', 'Catch Promo & Special Prices')}
                                    </h3>
                                    <p className="text-gray-600">
                                        {t('intro.promoAlerts.description', 'Get alerts for promotions and special offers. Never miss a discount on your favorite subscriptions.')}
                                    </p>
                                </div>
                            </div>

                            {/* Completely Free */}
                            <div className="flex items-start">
                                <div className="bg-green-100 p-3 rounded-lg mr-4 flex-shrink-0">
                                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                        {t('intro.startFree.title', 'Start Free')}
                                    </h3>
                                    <p className="text-gray-600">
                                        {t('intro.startFree.description', 'Basic features are completely free. Upgrade to Premium for advanced analytics and unlimited tracking.')}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <Link 
                            to="/demo" 
                            className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-blue-900 hover:bg-blue-800 rounded-2xl shadow-xl transition-all duration-300 transform hover:-translate-y-1 group"
                        >
                            {t('intro.getStarted', 'Get Started Free')}
                            <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Intro;